import {useSelector} from 'react-redux';

/**
 * Hook for managing integration status.
 * Fetches active integrations and provides a function to check if an integration is active.
 * 
 * @returns {Object} Object containing isIntegrationActive function to check if an integration is active
 */
export const useIntegrationStatus = () => {
  const { activeIntegrations = [] } = useSelector((state: any) => state.organization);

  /**
   * Check if an integration is active based on its type
   * 
   * @param {string} integrationType - The type of integration to check (e.g., 'azure', 'jira')
   * @returns {boolean} True if the integration is active, false otherwise
   */
  const isIntegrationActive = (integrationType: string): boolean => {
    if (!integrationType) return false;
    
    return activeIntegrations.some(
      (integration: any) => 
        integration.integration_type === integrationType.toLowerCase() && 
        integration.is_active
    );
  };

  /**
   * Check if integration data should be shown based on the integration_data object
   * 
   * @param {Object} integrationData - The integration data object
   * @returns {boolean} True if integration data should be shown, false otherwise
   */
  const shouldShowIntegration = (integrationData: any): boolean => {
    if (!integrationData || !integrationData.integration_type) return false;
    
    return isIntegrationActive(integrationData.integration_type);
  };

  const isAnyOtherIntegrationActive = (currentType: string): boolean => {
    return activeIntegrations
      .filter((el: any) => el.integration_type !== "github" && el.integration_type !== "slack" && el.integration_type !== "llm")
      .some((integration: any) =>
        integration.is_active && integration.integration_type !== currentType
      );
  };

  return {
    activeIntegrations,
    isIntegrationActive,
    shouldShowIntegration,
    isAnyOtherIntegrationActive
  };
};

export default useIntegrationStatus; 